import { useEffect } from "react";

import { MobileRoutingOverlay } from "components/MobileRoutingOverlay";
import { HomeFooter } from "components/footer/HomeFooter";
import { MainHeader } from "components/header/MainHeader";
import { MobileMainHeader } from "components/header/MobileMainHeader";
import { OrgSelect } from "components/materials/home/OrgSelect";
import { RoleSelect } from "components/materials/home/RoleSelect";
import { PageSecondarySidebar } from "components/materials/page/PageSecondarySidebar";
import { SidebarOpenContainer } from "components/materials/sidebar/SidebarOpenContainer";
import { t } from "i18n/i18n";
import { useIsLgOrLarger, useIsMdOrLarger } from "mds/hooks/use-responsive";
import { LoadingDotsIcon } from "mds/icons";
import { useIsHomeRolesPending } from "providers/HomeRoleProvider";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import { selectCurrentSectionId, selectCurrentUser, selectIsStudentPreview } from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { useObjectSelector, usePromiseEffect } from "store/store-hooks";
import { trackUserProperty } from "utils/amplitude";
import { Outlet } from "utils/routing";
import { PROFILE_PROPERTIES } from "utils/user-utils";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

export default function Layout() {
  const dispatch = useAppDispatch();
  const isLargeScreen = useIsLgOrLarger();
  const isMobile = !useIsMdOrLarger();
  const isSmallScreen = !useIsMdOrLarger();
  const currentUser = useObjectSelector(selectCurrentUser, PROFILE_PROPERTIES);
  const userFirstName = currentUser?.first_name || currentUser?.last_name || "";
  const isHomeRolesPending = useIsHomeRolesPending();
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const currentSectionId = useAppSelector(selectCurrentSectionId);

  // We use isLoading to guarantee child routes that courses/orgs and related information is loaded
  const { isLoading: isHomeDataLoading, error } = usePromiseEffect(async () => {
    return Promise.all([
      storeApi.sections.list(),
      storeApi.courses.list(),
      storeApi.terms.list(),
      storeApi.class_sessions.list(),
      storeApi.outcomes.list(),
    ]);
  }, []);
  // Enrollments hydrate via HomeRoleProvider; gate so RoleSelect / section lists don't flash empty.
  const isLoading = isHomeDataLoading || isHomeRolesPending;

  useEffect(() => {
    if (currentSectionId) {
      dispatch(viewStateActions.setCurrentSectionId(null));
      trackUserProperty("courseId", null);
      trackUserProperty("courseRole", null);
    }
  }, [currentSectionId, dispatch]);

  useEffect(() => {
    if (isStudentPreview) {
      dispatch(viewStateActions.setIsStudentPreview(false));
    }
  }, [isStudentPreview, dispatch]);

  return (
    <div className="flex h-full flex-col">
      {isMobile ? <MobileMainHeader /> : <MainHeader />}
      <SidebarOpenContainer>
        <div className="relative h-full max-w-full grow">
          <ErrorBoundary>
            <MobileRoutingOverlay />
            <div className="flex h-full w-full flex-row items-stretch overflow-hidden bg-white">
              <div className="flex max-h-full w-full flex-col items-center gap-6 overflow-hidden bg-white p-6 pb-0 md:flex-row md:items-start md:justify-center md:gap-10 lg:gap-16">
                <div className="flex h-full w-full max-w-[1200px] flex-col gap-6">
                  <div className="flex w-full flex-wrap justify-between gap-2">
                    <h1 className="h2">
                      {userFirstName && t("home.header.welcome", { firstName: userFirstName })}
                    </h1>
                    <div className="flex items-center gap-4">
                      <OrgSelect />
                      <RoleSelect isLoading={isLoading} />
                    </div>
                  </div>
                  <div className="h-full overflow-hidden">
                    {error ? (
                      <div className="flex h-full w-full items-center justify-center">
                        <div className="text-red">Error: {error.message}</div>
                      </div>
                    ) : isLoading ? (
                      <div className="flex h-full w-full items-center justify-center">
                        <LoadingDotsIcon className="h-10 w-10" />
                      </div>
                    ) : (
                      <Outlet />
                    )}
                  </div>
                </div>
              </div>
              {!isSmallScreen && (
                <PageSecondarySidebar className="h-full min-w-[392px] max-w-[520px] flex-1 basis-1/3 overflow-y-auto px-3 py-4" />
              )}
            </div>
          </ErrorBoundary>
        </div>
      </SidebarOpenContainer>
      {isLargeScreen && <HomeFooter />}
    </div>
  );
}
